'use client';

import { useState, useEffect, memo } from 'react';
import { useRouter } from 'next/navigation';
import { usePriceConvert } from '@/lib/PriceContext';
import { syncWishlistCookie } from '@/lib/clientStore';
import Link from 'next/link';
import { Heart } from 'lucide-react';
import type { CatalogIndexItem } from '@/types/catalog';
import { genreLabel } from '@/lib/genreLabel';
import { offerLinkSuffix, type RegionCode } from './OfferFilter';

// Module-level Set - prefetch кожен продукт лише раз за сесію
const prefetched = new Set<string>();

const PLATFORM_MAP: Record<string, { label: string; cls: string; svg?: boolean }> = {
  XBOXSERIESX: { label: 'X|S',    cls: 'ptag-xsx' },
  XBOXONE:     { label: 'ONE',    cls: 'ptag-xone' },
  PC:          { label: 'PC',     cls: 'ptag-pc' },
  XBOX360:     { label: '360',    cls: 'ptag-360' },
  XCLOUD:      { label: 'XCLOUD', cls: 'ptag-cloud', svg: true },
};

const CloudIcon = () => (
  <svg viewBox="0 0 32 22" width="13" height="9" fill="currentColor" aria-label="Xbox Cloud Gaming" style={{ display: 'block' }}>
    <path d="M26 9a6 6 0 0 0-1-.08A8 8 0 0 0 10.1 7.4 5 5 0 1 0 5 17h21a5 5 0 1 0 0-10c-.34 0-.68.03-1 .08A5.99 5.99 0 0 0 26 9z"/>
  </svg>
);

function fmtPrice(val: number | null, currency: string): string {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
}

function isInWishlist(id: string): boolean {
  try {
    const raw = localStorage.getItem('wishlist');
    if (!raw) return false;
    return JSON.parse(raw).some((i: { product_id: string }) => i.product_id === id);
  } catch { return false; }
}

function CatalogCardImpl({ item, cardMin = 240, lang = 'en', offerType = 'key', region = 'any', priceUsdOverride }: {
  item: CatalogIndexItem; cardMin?: number; lang?: string;
  offerType?: 'key' | 'gift' | 'account' | 'rental';
  region?: RegionCode;
  priceUsdOverride?: number | null;
}) {
  // Deep-link: лінк на PDP несе поточний тип видачі + регіон (?offer=&region=).
  const hrefSuffix = offerLinkSuffix(offerType, region);
  const disc = item.discount_pct ? Math.round(Math.abs(item.discount_pct)) : null;
  const { convert: fmtConverted } = usePriceConvert();
  // Price by offer type — falls back to global min
  const displayPriceUsd =
    priceUsdOverride !== undefined ? priceUsdOverride
    : offerType === 'gift'    ? (item.min_gift_usd    ?? item.price)
    : offerType === 'account' ? (item.min_account_usd ?? item.price)
    : offerType === 'rental'  ? (item.min_rental_usd  ?? item.price)
    : (item.min_key_usd ?? item.price);
  // poster_w400 is derived from w300 (catalog-index omits it — same URL, _w300.→_w400.)
  const posterW400 = item.poster_w400 ?? (item.poster_w300 ? item.poster_w300.replace('_w300.', '_w400.') : null);
  const artUrl = (cardMin >= 260 && posterW400) ? posterW400 : item.poster_w300;
  const isGame = item.type === 'game';
  const typeLabel = isGame ? 'GAME' : 'DLC';
  const displayTitle = (lang === 'ru' && item.title_ru) ? item.title_ru
    : (lang === 'uk' && (item as any).title_uk) ? (item as any).title_uk
    : item.title;

  const platformKeys = item.is_xbox360
    ? ['XBOX360', ...(item.platforms ?? [])]
    : (item.platforms ?? []);
  const platforms = platformKeys
    .map(p => PLATFORM_MAP[p])
    .filter(Boolean) as { label: string; cls: string; svg?: boolean }[];

  const router = useRouter();
  const [wished, setWished] = useState(false);

  function handleMouseEnter() {
    if (!prefetched.has(item.product_id)) {
      prefetched.add(item.product_id);
      router.prefetch(`/product/${item.product_id}`);
    }
  }

  useEffect(() => {
    setWished(isInWishlist(item.product_id));
    const sync = () => setWished(isInWishlist(item.product_id));
    window.addEventListener('wishlist-updated', sync);
    return () => window.removeEventListener('wishlist-updated', sync);
  }, [item.product_id]);

  function toggleWishlist(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    try {
      const raw = localStorage.getItem('wishlist');
      const wl: { product_id: string }[] = raw ? JSON.parse(raw) : [];
      const exists = wl.some(i => i.product_id === item.product_id);
      const next = exists
        ? wl.filter(i => i.product_id !== item.product_id)
        : [...wl, {
            product_id:       item.product_id,
            title:            item.title,
            title_ru:         item.title_ru ?? null,
            title_uk:         (item as any).title_uk ?? null,
            thumb_url:        artUrl ?? '',
            product_type:     item.type,
            market_code:      'US',
            currency_code:    item.currency,
            list_price:       item.price,
            msrp:             item.msrp,
            discount_percent: disc,
            offer_end_utc:    null,
            rating:           item.rating,
            platforms:        item.platforms ?? [],
            capabilities:     item.capabilities ?? [],
            developer_name:   item.developer_name,
            release_year:     item.release_year,
            offer_type:       'key' as const,
            rent_days:        null,
            added_at:         new Date().toISOString(),
          }];
      localStorage.setItem('wishlist', JSON.stringify(next));
      setWished(!exists);
      window.dispatchEvent(new Event('wishlist-updated'));
    } catch {}
  }

  return (
    <Link href={`/product/${item.product_id}${hrefSuffix}`} className="cat-card" style={{ textDecoration: 'none' }} onMouseEnter={handleMouseEnter}>
      {/* Art */}
      <div className="cat-art-wrap">
        <div className="cat-art-bg">
          {artUrl
            ? <img src={artUrl} alt={item.title} loading="lazy" />
            : <div style={{ width: '100%', height: '100%', background: '#1a1a22' }} />
          }
        </div>

        {/* Type badge */}
        <span className={`badge-pill badge-type ${isGame ? 'badge-game' : 'badge-dlc'}`}>
          {typeLabel}
        </span>

        {/* Discount badge */}
        {disc != null && disc > 0 && (
          <span className="badge-pill badge-disc">−{disc}%</span>
        )}

        {/* Platform tags */}
        {platforms.length > 0 && (
          <div className="cat-platforms">
            {platforms.map(p => (
              <span key={p.label} className={`platform-tag ${p.cls}`}>
                {p.svg ? <CloudIcon /> : p.label}
              </span>
            ))}
          </div>
        )}

        {/* Wishlist overlay */}
        <div className="cat-wish-overlay">
          <button
            className={`cat-wish-btn${wished ? ' wished' : ''}`}
            aria-label={wished ? 'Видалити з бажаних' : 'Додати до бажаних'}
            onClick={toggleWishlist}
          >
            <Heart size={16} />
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="cat-info">
        <div className="cat-name" title={displayTitle}>{displayTitle}</div>
        <div className="cat-genre">{genreLabel(item.category, lang)}</div>
        <div className="cat-price-row">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span className="cat-price">
              {fmtConverted(displayPriceUsd) ?? fmtPrice(displayPriceUsd, item.currency)}
            </span>
            {disc != null && disc > 0 && item.msrp != null && (
              <span className="cat-price-orig">
                {fmtConverted(item.msrp) ?? fmtPrice(item.msrp, item.currency)}
              </span>
            )}
          </div>
          {item.rating > 0 && (
            <div className="cat-rating">
              <span style={{ color: '#f59e0b' }}>★</span>
              <span>{item.rating.toFixed(1)}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

// Memoized: prevents every card from re-rendering on unrelated parent updates
// (e.g. opening/closing the genre panel) — that mass re-render caused the freeze.
export const CatalogCard = memo(CatalogCardImpl);
